/**
 * university_campus.js — Campus Placement
 * ─────────────────────────────────────────────────────────────────────────────
 * Design Philosophy:
 *   Goal:         Highlight education, academic achievements, and tech stack for graduates
 *   Hierarchy:    Education (sidebar first) → Projects (cards) → Internships → Skills (tags)
 *   Divider:      Pill — soft, modern capsule shapes for a friendly, approachable layout
 *   Density:      Comfortable — balanced page layout, easy to scan during campus drives
 *   Typography:   Source Sans Pro primary & secondary (approachable and readable sans-serif)
 *   Audience:     College Students, Fresh Graduates, Interns, Entry-Level Candidates
 *
 * Visual Signature:
 *   - Two-column layout with left sidebar holding Education details and CGPA prominently
 *   - Projects rendered as cards with bordered outlines in the main column (projects-cards)
 *   - Skills rendered as tags in main (skills-tags)
 *   - Energetic Royal Blue accents (#1D4ED8) with a soft blue sidebar tint (#EFF6FF)
 */

export const university_campus = {
  id: 'university-campus',
  version: 3,
  renderer: 'enterprise',
  name: 'Campus Placement',
  layout: 'two-column',
  theme: 'classic-navy',
  density: 'comfortable',
  sectionDividerStyle: 'pill',
  sectionOrder: ['personal-info', 'education', 'projects', 'internships', 'skills', 'achievements', 'certifications', 'languages'],
  components: {
    header: 'header-centered',
    experience: 'experience-compact',
    projects: 'projects-cards',
    skills: 'skills-tags',
    education: 'education-sidebar',
  },
  layouts: {
    'two-column': {
      component: 'two-column',
      slots: {
        header: ['personal-info'],
        sidebar: ['education', 'certifications', 'languages', 'achievements'],
        main: ['projects', 'internships', 'skills'],
      },
      overrides: {
        '--font-family-primary': '"Source Sans Pro", "Calibri", "Open Sans", Arial, sans-serif',
        '--font-family-secondary': '"Source Sans Pro", "Calibri", "Open Sans", Arial, sans-serif',
        '--color-primary': '#1D4ED8', // Royal Blue
        '--color-secondary': '#1E40AF',
        '--color-accent': '#3B82F6',
        '--color-text-main': '#1E293B',
        '--color-text-light': '#64748B',
        '--color-border': '#BFDBFE',
        '--color-bg-page': '#FFFFFF',
        '--layout-sidebar-width': '230px',
        '--layout-column-gap': '20px',
        '--layout-sidebar-bg': '#EFF6FF', // Friendly light blue sidebar background
        '--layout-sidebar-padding': '14px',
        '--layout-sidebar-border': '1px solid #BFDBFE',
        '--spacing-margin': '22px',
        '--spacing-section-mb': '16px',
        '--spacing-item-mb': '12px',
        '--spacing-bullet-gap': '4px',
      },
    },
  },
  styleOverrides: {
    '--font-size-display': '23pt',
    '--weight-display': '700',
    '--heading-weight': '600',
    '--heading-text-transform': 'none',
    '--heading-letter-spacing': '0.02em',
    '--border-thickness-heading': '0',
    '--bullet-style': 'disc',
    '--font-size-heading': '9.5pt',
    '--font-size-body': '9pt',
    '--font-size-bullet': '8.8pt',
    '--line-height-body': '1.5',
  },
};

export default university_campus;
